import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Wand2, ShieldCheck, Send } from "lucide-react";
import { BookDemoButton } from "@/pages/marketing/MarketingLayout";

const HERO_BG =
  "https://images.unsplash.com/photo-1556761175-4b46a572b786?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBjbGVhbiUyMG9mZmljZSUyMGJ1c2luZXNzfGVufDB8fHx8MTc4NzczODg5OHww&ixlib=rb-4.1.0&q=85";

const FEATURES = [
  { icon: Wand2, title: "AI Creative Director", desc: "Chat with 'Shee' to brainstorm, draft, and visualize your next viral campaign.", accent: "text-blue-600 bg-blue-50" },
  { icon: ShieldCheck, title: "Approval Queue", desc: "Role-based workflows. Staff reviews AI-drafted posts before anything goes live.", accent: "text-emerald-600 bg-emerald-50" },
  { icon: Send, title: "One-Click Publishing", desc: "Direct integrations with Facebook, Instagram, LinkedIn, TikTok, and YouTube.", accent: "text-blue-600 bg-blue-50" },
];

const STEPS = [
  { n: "01", title: "Ideate with AI", desc: "Shee drafts tailored content for your brand voice." },
  { n: "02", title: "Review & Refine", desc: "Your team approves via the Queue — edits welcome." },
  { n: "03", title: "Publish & Grow", desc: "Content goes live across all platforms instantly." },
];

export default function Home() {
  return (
    <div data-testid="marketing-home-page">
      <section className="relative overflow-hidden bg-slate-900">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/70 via-slate-900/85 to-slate-900" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 pt-28 pb-32 lg:pt-36 lg:pb-40 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-7">
          <motion.span
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-4 py-1.5 text-xs font-semibold text-white/90 tracking-wide mb-8"
            data-testid="hero-tagline-pill"
          >
            <span className="text-blue-300">AI-powered.</span>
            <span className="w-1 h-1 rounded-full bg-white/40" />
            <span className="text-emerald-300">Human-approved.</span>
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tighter max-w-3xl leading-[1.05]"
            style={{ fontFamily: "Outfit" }}
            data-testid="hero-title"
          >
            Social Media Management, Perfected for Your Brand.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-slate-300 text-base md:text-lg mt-6 max-w-xl leading-relaxed"
            data-testid="hero-subtitle"
          >
            Meet Shee, your AI Creative Director. Generate captions, images, and videos
            conversationally, but stay in complete control. Nothing publishes until a human approves it.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-10 flex flex-wrap items-center gap-6"
          >
            <Link
              to="/about#contact"
              data-testid="hero-book-demo-button"
              className="inline-flex items-center gap-2 rounded-full bg-white text-slate-900 px-7 py-3.5 text-sm font-bold tracking-tight hover:bg-slate-100 transition-colors"
              style={{ fontFamily: "Outfit" }}
            >
              Book a Demo <ArrowUpRight size={16} strokeWidth={2.5} />
            </Link>
            <Link to="/features" data-testid="hero-see-features-link" className="text-white/80 text-sm font-medium hover:text-white transition-colors">
              See how it works →
            </Link>
          </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="hidden lg:block lg:col-span-5"
            data-testid="hero-approval-preview-card"
          >
            <div className="rounded-3xl bg-white/5 border border-white/10 backdrop-blur-xl p-6 shadow-2xl">
              <p className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-4">Approval Queue</p>
              <div className="space-y-3">
                {[
                  { label: "Summer skincare reel", tag: "AI · Needs review", tagColor: "text-blue-300 bg-blue-400/10" },
                  { label: "Client testimonial post", tag: "Approved", tagColor: "text-emerald-300 bg-emerald-400/10" },
                  { label: "Weekend promo carousel", tag: "AI · Needs review", tagColor: "text-blue-300 bg-blue-400/10" },
                ].map((row) => (
                  <div key={row.label} className="flex items-center justify-between gap-3 rounded-2xl bg-white/5 border border-white/10 px-4 py-3">
                    <span className="text-sm text-white/80 truncate">{row.label}</span>
                    <span className={`text-[10px] font-semibold px-2 py-1 rounded-full whitespace-nowrap ${row.tagColor}`}>{row.tag}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-white/40 mt-5 leading-relaxed">
                Every post — human or AI — waits here until someone on your team says yes.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-24">
        <div className="max-w-2xl mb-14">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit" }}>
            Everything you need to scale content without losing your voice.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="border border-slate-200 rounded-3xl p-8 bg-white hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow] duration-300"
                data-testid={`home-feature-card-${i}`}
              >
                <div className={`w-11 h-11 rounded-2xl flex items-center justify-center mb-6 ${f.accent}`}>
                  <Icon size={20} strokeWidth={2} />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2 tracking-tight" style={{ fontFamily: "Outfit" }}>
                  {f.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      <section className="bg-slate-50 py-24">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight mb-14" style={{ fontFamily: "Outfit" }}>
            How PYC Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.12 }}
                data-testid={`how-it-works-step-${i}`}
              >
                <div className="text-5xl font-black text-slate-200 mb-4" style={{ fontFamily: "Outfit" }}>
                  {s.n}
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2 tracking-tight" style={{ fontFamily: "Outfit" }}>
                  {s.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-24">
        <div className="rounded-3xl bg-slate-900 px-8 md:px-16 py-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight mb-3" style={{ fontFamily: "Outfit" }}>
              Ready to see Shee in action?
            </h2>
            <p className="text-slate-400 max-w-md">
              Personalized onboarding, no self-serve guesswork — book a demo and we'll set it up together.
            </p>
          </div>
          <BookDemoButton testId="cta-section-book-demo-button" className="shrink-0 !bg-white !text-slate-900 hover:!bg-slate-100" />
        </div>
      </section>
    </div>
  );
}
